import type { LevelSpec } from '../../core/chart';
import { faseSpec } from '../../core/track-chart';
import type { FaseOverrides } from '../../core/track-chart';
import { barsOf } from './score';
import { seedOf } from './worlds';

export type FaseTrack = {
  id: string;
  world: number;
  worldName: string;
  fase: number;
  faseName: string;
  boss: boolean;
  template: string;
  seed: string;
  bpm: number;
  bars: number;
  seconds: number;
  energy: number;
  level: number;
  overrides: FaseOverrides;
  coach: number;
  safe: boolean;
  spec: string;
  file: string;
};

type FaseRow = {
  name: string;
  bpm: number;
  bars: number;
  energy: number;
  level: number;
  template?: string;
  overrides?: FaseOverrides;
  coach?: number;
  safe?: boolean;
};

export const COACH_ALL = Number.POSITIVE_INFINITY;

type WorldRow = { n: number; name: string; template: string; fases: readonly FaseRow[] };

const PI = Math.PI;

export const FASE_WORLDS: readonly WorldRow[] = [
  {
    n: 1,
    name: 'Arraial',
    template: 'forro',
    fases: [
      { name: 'Círculo', bpm: 120, bars: 24, energy: 0.55, level: 1, overrides: { holds: [4], grid: 8, hop: [0.15, 0.3], turn: PI / 4, slack: 2, still: 3 }, coach: COACH_ALL, safe: true },
      { name: 'Tamanhos', bpm: 122, bars: 24, energy: 0.62, level: 2, overrides: { rest: 2 }, coach: 4 },
      { name: 'Corrida', bpm: 124, bars: 24, energy: 0.7, level: 3, coach: 2 },
      { name: 'Quadrilha', bpm: 128, bars: 28, energy: 0.9, level: 3, overrides: { turn: (5 * PI) / 6, hop: [0.3, 0.6], slack: 1.5 } },
    ],
  },
  {
    n: 2,
    name: 'Céu desenhado',
    template: 'dream',
    fases: [
      { name: 'Órbita', bpm: 118, bars: 28, energy: 0.55, level: 1, overrides: { holds: [4], grid: 4, rest: 1, hop: [0.3, 0.55], turn: PI / 4, slack: 1.6 } },
      { name: 'Constelação', bpm: 120, bars: 28, energy: 0.65, level: 2, overrides: { holds: [4, 6, 8], grid: 2, rest: 1, hop: [0.35, 0.65], turn: PI / 4, slack: 1.5 } },
      { name: 'Cometa', bpm: 124, bars: 32, energy: 0.9, level: 3, overrides: { holds: [2, 4, 8], grid: 1, rest: 1, hop: [0.4, 0.8], turn: PI / 3, slack: 1.5 } },
    ],
  },
  {
    n: 3,
    name: 'Caderno',
    template: 'lofi',
    fases: [
      { name: 'Rabisco', bpm: 112, bars: 28, energy: 0.55, level: 1, overrides: { holds: [3], grid: 4, rest: 2, hop: [0.12, 0.28], turn: PI / 2, slack: 1.8 } },
      { name: 'Pontilhado', bpm: 116, bars: 28, energy: 0.65, level: 2, overrides: { holds: [2, 3, 4], grid: 2, rest: 2, hop: [0.12, 0.3], turn: (2 * PI) / 3, slack: 1.7 } },
      { name: 'Caligrafia', bpm: 120, bars: 32, energy: 0.9, level: 3, overrides: { holds: [2, 3, 4], grid: 1, rest: 1, hop: [0.15, 0.35], turn: (2 * PI) / 3, slack: 1.6 } },
    ],
  },
  {
    n: 4,
    name: 'Igarapé',
    template: 'carimbo',
    fases: [
      { name: 'Vitória-régia', bpm: 120, bars: 28, energy: 0.55, level: 1, overrides: { holds: [3], grid: 4, rest: 1, hop: [0.2, 0.35], turn: (2 * PI) / 3, slack: 1.6 } },
      { name: 'Remanso', bpm: 122, bars: 28, energy: 0.65, level: 2, overrides: { holds: [2, 4, 6], grid: 2, rest: 1, hop: [0.2, 0.4], turn: (5 * PI) / 6, slack: 1.5 } },
      { name: 'Correnteza', bpm: 126, bars: 32, energy: 0.75, level: 3, overrides: { holds: [2, 3, 4], grid: 1, rest: 1, hop: [0.25, 0.45], turn: (5 * PI) / 6, slack: 1.5 } },
      { name: 'Pororoca', bpm: 128, bars: 36, energy: 0.9, level: 3, overrides: { holds: [2, 3, 4, 6], grid: 1, rest: 1, hop: [0.3, 0.6], turn: (5 * PI) / 6, slack: 1.5 } },
    ],
  },
  {
    n: 5,
    name: 'Cogumelos',
    template: 'cogumelos',
    fases: [
      { name: 'Clareira', bpm: 118, bars: 28, energy: 0.55, level: 1, overrides: { holds: [2, 3], grid: 2, rest: 1, hop: [0.15, 0.35], turn: PI / 2, slack: 1.6 } },
      { name: 'Esporos', bpm: 122, bars: 28, energy: 0.65, level: 2, overrides: { holds: [2, 2, 2, 6], grid: 2, rest: 1, hop: [0.15, 0.4], turn: PI / 2, slack: 1.5 } },
      { name: 'Anel de fadas', bpm: 124, bars: 32, energy: 0.9, level: 3, overrides: { holds: [2, 2, 3, 6], grid: 1, rest: 1, hop: [0.2, 0.45], turn: (2 * PI) / 3, slack: 1.5 } },
    ],
  },
  {
    n: 6,
    name: 'Fundo do Mar',
    template: 'dub',
    fases: [
      { name: 'Maré mansa', bpm: 116, bars: 28, energy: 0.55, level: 1, overrides: { holds: [4, 6], grid: 4, rest: 2, hop: [0.2, 0.4], turn: PI / 3, slack: 1.6 } },
      { name: 'Água-viva', bpm: 120, bars: 28, energy: 0.65, level: 2, overrides: { holds: [2, 6, 10], grid: 2, rest: 1, hop: [0.2, 0.5], turn: PI / 2, slack: 1.5 } },
      { name: 'Abismo', bpm: 124, bars: 32, energy: 0.9, level: 3, overrides: { holds: [2, 4, 8, 12], grid: 1, rest: 1, hop: [0.25, 0.6], turn: (2 * PI) / 3, slack: 1.5 } },
    ],
  },
  {
    n: 7,
    name: 'Baile na Laje',
    template: 'funk',
    fases: [
      { name: 'Esquenta', bpm: 128, bars: 28, energy: 0.6, level: 2, overrides: { holds: [2, 4], grid: 2, rest: 1, hop: [0.2, 0.45], turn: PI / 2, slack: 1.6 } },
      { name: 'Tamborzão', bpm: 130, bars: 28, energy: 0.7, level: 3, overrides: { holds: [2, 3, 4], grid: 1, rest: 1, hop: [0.25, 0.55], turn: (2 * PI) / 3, slack: 1.5 } },
      { name: 'Paredão', bpm: 132, bars: 32, energy: 0.8, level: 3, overrides: { holds: [2, 2, 3, 4], grid: 1, rest: 1, hop: [0.3, 0.7], turn: (2 * PI) / 3, slack: 1.5 } },
      { name: 'Baile inteiro', bpm: 134, bars: 40, energy: 0.9, level: 3, overrides: { holds: [2, 3, 4, 6], grid: 1, rest: 1, hop: [0.3, 0.75], turn: (5 * PI) / 6, slack: 1.5 } },
    ],
  },
  {
    n: 8,
    name: 'Cristais',
    template: 'cristais',
    fases: [
      { name: 'Geodo', bpm: 122, bars: 28, energy: 0.55, level: 2, overrides: { holds: [2, 4, 6], grid: 2, hop: [0.2, 0.45], turn: PI / 2, slack: 1.5, shapes: ['circle', 'hex'] } },
      { name: 'Facetas', bpm: 124, bars: 28, energy: 0.65, level: 3, overrides: { holds: [2, 3, 4, 6], grid: 1, hop: [0.25, 0.6], turn: (2 * PI) / 3, slack: 1.5, shapes: ['circle', 'hex', 'square'] } },
      { name: 'Prisma', bpm: 124, bars: 32, energy: 0.9, level: 4, overrides: { shapes: ['circle', 'square', 'triangle', 'hex'], rotate: true, slack: 1.5 } },
    ],
  },
  {
    n: 9,
    name: 'Cordel',
    template: 'xote',
    fases: [
      { name: 'Xilo', bpm: 116, bars: 28, energy: 0.6, level: 2, overrides: { holds: [2, 4, 6], grid: 2, hop: [0.2, 0.45], turn: PI / 2, slack: 1.5, shapes: ['circle', 'square'] } },
      { name: 'Repente', bpm: 118, bars: 28, energy: 0.7, level: 3, overrides: { holds: [2, 3, 4], grid: 1, hop: [0.25, 0.55], turn: (2 * PI) / 3, slack: 1.5, shapes: ['circle', 'square', 'triangle'] } },
      { name: 'Peleja', bpm: 122, bars: 32, energy: 0.9, level: 4, overrides: { hop: [0.3, 0.7], slack: 1.5, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: true } },
    ],
  },
  {
    n: 10,
    name: 'Relojoaria',
    template: 'relojoaria',
    fases: [
      { name: 'Engrenagem', bpm: 120, bars: 28, energy: 0.55, level: 2, overrides: { holds: [2, 4, 6], grid: 2, hop: [0.2, 0.45], turn: PI / 2, shapes: ['circle', 'hex'], rotate: false, slack: 1.5 } },
      { name: 'Catraca', bpm: 124, bars: 28, energy: 0.7, level: 4, overrides: { holds: [2, 3, 4, 6], grid: 1, shapes: ['circle', 'hex', 'square'], rotate: true, slack: 1.5 } },
      { name: 'Corda cheia', bpm: 128, bars: 36, energy: 0.9, level: 4, overrides: { hop: [0.3, 0.7], turn: (2 * PI) / 3, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: true, slack: 1.5 } },
    ],
  },
  {
    n: 11,
    name: 'Bloco',
    template: 'bateria',
    fases: [
      { name: 'Concentração', bpm: 130, bars: 28, energy: 0.55, level: 3, overrides: { holds: [2, 3, 4, 6], grid: 1, slack: 1.5 } },
      { name: 'Paradinha', bpm: 130, bars: 32, energy: 0.7, level: 5, overrides: { dipOdds: 0.2, shapes: ['circle'], rotate: false, slack: 1.5 } },
      { name: 'Bateria', bpm: 134, bars: 36, energy: 0.8, level: 5, overrides: { dipOdds: 0.35, shapes: ['circle'], rotate: false, slack: 1.5 } },
      { name: 'Avenida', bpm: 136, bars: 40, energy: 0.9, level: 5, overrides: { dipOdds: 0.35, shapes: ['circle', 'square', 'hex'], rotate: false, slack: 1.5 } },
    ],
  },
  {
    n: 12,
    name: 'Lanternas',
    template: 'lanternas',
    fases: [
      { name: 'Lago', bpm: 116, bars: 28, energy: 0.55, level: 2, overrides: { holds: [4, 6], grid: 2, slack: 1.5 } },
      { name: 'Vento', bpm: 118, bars: 28, energy: 0.65, level: 5, overrides: { holds: [4, 6, 8], dipOdds: 0.2, shapes: ['circle'], rotate: false, slack: 1.5 } },
      { name: 'Festival', bpm: 120, bars: 32, energy: 0.75, level: 5, overrides: { holds: [2, 4, 6, 8], grid: 2, hop: [0.25, 0.6], dipOdds: 0.35, shapes: ['circle'], rotate: false, slack: 1.5 } },
      { name: 'Céu de lanternas', bpm: 124, bars: 36, energy: 0.9, level: 5, overrides: { grid: 1, dipOdds: 0.35, shapes: ['circle', 'hex'], rotate: false, slack: 1.5 } },
    ],
  },
  {
    n: 13,
    name: 'Fliperama',
    template: 'chip',
    fases: [
      { name: 'Ficha', bpm: 124, bars: 28, energy: 0.55, level: 3, overrides: { slack: 1.5 } },
      { name: 'Bumper', bpm: 124, bars: 36, energy: 0.65, level: 6, overrides: { stabOdds: 0.15, shapes: ['circle'], rotate: false, dipOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Multibola', bpm: 128, bars: 32, energy: 0.75, level: 6, overrides: { stabOdds: 0.3, shapes: ['circle'], rotate: false, dipOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Tilt', bpm: 130, bars: 40, energy: 0.9, level: 6, overrides: { stabOdds: 0.3, dipOdds: 0.2, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: false, blindOdds: 0, slack: 1.5 } },
    ],
  },
  {
    n: 14,
    name: 'Frevo',
    template: 'frevo',
    fases: [
      { name: 'Passo', bpm: 132, bars: 32, energy: 0.65, level: 3, overrides: { slack: 1.5 } },
      { name: 'Tesoura', bpm: 134, bars: 32, energy: 0.72, level: 6, overrides: { stabOdds: 0.2, shapes: ['circle'], rotate: false, dipOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Ferver', bpm: 136, bars: 32, energy: 0.8, level: 6, overrides: { stabOdds: 0.3, shapes: ['circle'], rotate: false, dipOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Multidão', bpm: 138, bars: 36, energy: 0.9, level: 6, overrides: { stabOdds: 0.35, dipOdds: 0.2, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: false, blindOdds: 0, slack: 1.5 } },
    ],
  },
  {
    n: 15,
    name: 'Cidade na Chuva',
    template: 'chuva',
    fases: [
      { name: 'Garoa', bpm: 116, bars: 28, energy: 0.55, level: 3, overrides: { slack: 1.5 } },
      { name: 'Neblina', bpm: 118, bars: 28, energy: 0.65, level: 7, overrides: { blindOdds: 0.15, shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, slack: 1.5 } },
      { name: 'Temporal', bpm: 120, bars: 28, energy: 0.75, level: 7, overrides: { blindOdds: 0.25, shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, slack: 1.5 } },
      { name: 'Tempestade', bpm: 120, bars: 32, energy: 0.9, level: 7, overrides: { blindOdds: 0.25, dipOdds: 0.2, stabOdds: 0, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: false, slack: 1.5 } },
    ],
  },
  {
    n: 16,
    name: 'Maracatu',
    template: 'maracatu',
    fases: [
      { name: 'Cortejo', bpm: 112, bars: 28, energy: 0.55, level: 3, overrides: { holds: [2, 4], grid: 2, slack: 1.5 } },
      { name: 'Baque', bpm: 116, bars: 28, energy: 0.65, level: 7, overrides: { blindOdds: 0.25, shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, slack: 1.5 } },
      { name: 'Loas', bpm: 118, bars: 28, energy: 0.75, level: 7, overrides: { blindOdds: 0.35, shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, slack: 1.5 } },
      { name: 'Nação', bpm: 120, bars: 32, energy: 0.9, level: 7, overrides: { blindOdds: 0.35, stabOdds: 0.2, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: false, dipOdds: 0, slack: 1.5 } },
    ],
  },
  {
    n: 17,
    name: 'Aurora',
    template: 'aurora',
    fases: [
      { name: 'Neve', bpm: 120, bars: 28, energy: 0.55, level: 3, overrides: { slack: 1.5 } },
      { name: 'Reflexo', bpm: 120, bars: 28, energy: 0.65, level: 8, overrides: { pairs: true, holds: [4], shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Aurora', bpm: 122, bars: 32, energy: 0.75, level: 8, overrides: { pairs: true, holds: [2, 4, 6], shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Polar', bpm: 124, bars: 36, energy: 0.9, level: 8, overrides: { pairs: true, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: false, dipOdds: 0, stabOdds: 0, blindOdds: 0, slack: 1.5 } },
    ],
  },
  {
    n: 18,
    name: 'Roda de Samba',
    template: 'samba',
    fases: [
      { name: 'Partido-alto', bpm: 112, bars: 24, energy: 0.55, level: 3, overrides: { holds: [2, 4], grid: 2, slack: 1.5 } },
      { name: 'Resposta', bpm: 116, bars: 28, energy: 0.7, level: 8, overrides: { pairs: true, holds: [2, 4], shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Roda cheia', bpm: 120, bars: 32, energy: 0.8, level: 8, overrides: { pairs: true, holds: [2, 3, 4, 6], grid: 1, shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Pagode', bpm: 124, bars: 36, energy: 0.9, level: 8, overrides: { pairs: true, dipOdds: 0.2, shapes: ['circle'], rotate: false, stabOdds: 0, blindOdds: 0, slack: 1.5 } },
    ],
  },
  {
    n: 19,
    name: 'Doces',
    template: 'pop',
    fases: [
      { name: 'Bala', bpm: 120, bars: 28, energy: 0.55, level: 3, overrides: { slack: 1.5 } },
      { name: 'Chiclete', bpm: 120, bars: 32, energy: 0.62, level: 3, overrides: { curve: 'exp', holds: [4, 6], grid: 2, slack: 1.5 } },
      { name: 'Bolha', bpm: 122, bars: 32, energy: 0.7, level: 3, overrides: { curve: 'exp', holds: [2, 4, 6, 8], grid: 1, slack: 1.5 } },
      { name: 'Confeitaria', bpm: 124, bars: 36, energy: 0.9, level: 6, overrides: { curve: 'exp', stabOdds: 0.2, shapes: ['circle'], rotate: false, dipOdds: 0, blindOdds: 0, slack: 1.5 } },
    ],
  },
  {
    n: 20,
    name: 'Discoteca',
    template: 'disco',
    fases: [
      { name: 'Vinil', bpm: 120, bars: 28, energy: 0.55, level: 3, overrides: { slack: 1.5 } },
      { name: 'Scratch', bpm: 120, bars: 32, energy: 0.62, level: 3, overrides: { curve: 'stutter', holds: [4], grid: 2, slack: 1.5 } },
      { name: 'Pista', bpm: 124, bars: 32, energy: 0.7, level: 3, overrides: { curve: 'stutter', holds: [2, 4, 6], grid: 1, slack: 1.5 } },
      { name: 'Globo', bpm: 124, bars: 36, energy: 0.9, level: 8, overrides: { curve: 'stutter', pairs: true, shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, blindOdds: 0, slack: 1.5 } },
    ],
  },
  {
    n: 21,
    name: 'Abóboras',
    template: 'spooky',
    fases: [
      { name: 'Horta', bpm: 120, bars: 28, energy: 0.55, level: 5, overrides: { dipOdds: 0.2, stabOdds: 0, blindOdds: 0, shapes: ['circle'], rotate: false, slack: 1.5 } },
      { name: 'Névoa', bpm: 122, bars: 28, energy: 0.62, level: 7, overrides: { blindOdds: 0.2, dipOdds: 0, stabOdds: 0, shapes: ['circle'], rotate: false, slack: 1.5 } },
      { name: 'Meia-noite', bpm: 124, bars: 28, energy: 0.7, level: 7, overrides: { blindOdds: 0.25, dipOdds: 0.35, stabOdds: 0, shapes: ['circle'], rotate: false, slack: 1.5 } },
      { name: 'Lua cheia', bpm: 128, bars: 32, energy: 0.9, level: 7, overrides: { blindOdds: 0.3, dipOdds: 0.35, stabOdds: 0.2, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: false, slack: 1.5 } },
    ],
  },
  {
    n: 22,
    name: 'Orla',
    template: 'sambareggae',
    fases: [
      { name: 'Calçadão', bpm: 122, bars: 28, energy: 0.55, level: 6, overrides: { stabOdds: 0.15, shapes: ['circle'], rotate: false, dipOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Maré', bpm: 124, bars: 28, energy: 0.65, level: 8, overrides: { pairs: true, shapes: ['circle'], rotate: false, dipOdds: 0, stabOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Quiosque', bpm: 126, bars: 28, energy: 0.75, level: 8, overrides: { pairs: true, stabOdds: 0.2, shapes: ['circle'], rotate: false, dipOdds: 0, blindOdds: 0, slack: 1.5 } },
      { name: 'Réveillon', bpm: 130, bars: 32, energy: 0.9, level: 8, overrides: { pairs: true, stabOdds: 0.3, dipOdds: 0.2, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: false, blindOdds: 0, slack: 1.5 } },
    ],
  },
  {
    n: 23,
    name: 'Grande Palco',
    template: 'funk',
    fases: [
      { name: 'Passagem de som', bpm: 128, bars: 28, energy: 0.8, level: 6, overrides: { stabOdds: 0.2, dipOdds: 0.2, blindOdds: 0, shapes: ['circle', 'square', 'triangle', 'hex'], rotate: false, slack: 1.5 } },
      { name: 'Bis', bpm: 134, bars: 32, energy: 0.85, level: 8, template: 'edm', overrides: { pairs: true, stabOdds: 0.3, dipOdds: 0.35, blindOdds: 0.2, shapes: ['circle', 'square', 'triangle', 'hex'], slack: 1.5 } },
      { name: 'Final', bpm: 140, bars: 40, energy: 0.9, level: 9, template: 'phonk' },
    ],
  },
];

export function faseOf(world: WorldRow, index: number): FaseTrack {
  const f = world.fases[index]!;
  const template = f.template ?? world.template;
  const seed = seedOf(f.name);
  const bars = barsOf({ bpm: f.bpm, bars: f.bars, energy: f.energy });
  return {
    id: `${seedOf(world.name)}-f${String(index + 1)}`,
    world: world.n,
    worldName: world.name,
    fase: index + 1,
    faseName: f.name,
    boss: index === world.fases.length - 1,
    template,
    seed,
    bpm: f.bpm,
    bars,
    seconds: (bars * 240) / f.bpm,
    energy: f.energy,
    level: f.level,
    overrides: { bpm: f.bpm, ...f.overrides },
    coach: f.coach ?? 0,
    safe: f.safe === true,
    spec: `${template}:${seed}:${String(f.bpm)}:${String(bars)}:${String(f.energy)}`,
    file: `${String(world.n)} ${world.name} - F${String(index + 1)} ${f.name}`,
  };
}

export const FASES: readonly FaseTrack[] = FASE_WORLDS.flatMap((w) => w.fases.map((_, i) => faseOf(w, i)));

export function faseTrack(id: string): FaseTrack {
  const f = FASES.find((x) => x.id === id);
  if (f === undefined) throw new Error(`no fase ${id}`);
  return f;
}

export function faseLevel(f: FaseTrack): LevelSpec {
  return faseSpec(f.level, f.overrides);
}
